import type { TrackerProduct } from './trackerProduct'
import type { StoreRegion } from '@/types/storeProducts.api'

/**
 * Upgrade offers: a device bought from the portal without a subscription, which then takes over the
 * subscription of a device the customer already owns (and keeps). Each offer has its landing page,
 * `/upgrade/<slug>`, linked from email campaigns. The Shopify SKUs keep their `_tradein` suffix.
 *
 * Eligibility, purchase limits and which devices may give their subscription are decided by the
 * backend. An offer only says what is sold, and names the products for the copy and the images.
 */

/** Where the customer has the order shipped, chosen on the landing page */
export const SHIPPING_DESTINATIONS = ['eu', 'ch', 'us', 'other'] as const
export type ShippingDestination = (typeof SHIPPING_DESTINATIONS)[number]

/**
 * Shopify product handle and variant SKU sold by the portal (backend SHOPIFY_PORTAL_PRODUCTS), and the
 * store it is sold from, which follows the delivery country
 */
export interface StoreProduct {
  handle: string
  sku: string
  store: StoreRegion
}

export interface UpgradeOffer {
  /** The product sold for each destination: a destination missing here falls back on `eu` */
  products: { eu: StoreProduct } & Partial<Record<ShippingDestination, StoreProduct>>
  /** Products whose subscription can move to the new one */
  sources: TrackerProduct[]
  /** The product sold */
  target: TrackerProduct
}

/** Keyed by landing page slug: a slug missing here is a 404 */
export const UPGRADE_OFFERS: Record<string, UpgradeOffer> = {
  // LWT3 → LWT7 "Nouvelle Génération"
  'pro-to-pro-max': {
    products: {
      eu: { handle: 'gps-tracker-pro-max', sku: '99970_tradein', store: 'eu' },
      ch: { handle: 'gps-tracker-pro-max-ch', sku: '99970_tradein-CH', store: 'eu' },
      us: { handle: 'gps-tracker-pro-max', sku: '99970_tradein', store: 'us' },
    },
    sources: ['pro'],
    target: 'proMax',
  },
}

/**
 * The checkout link only ever leads to the Shopify checkout: anything else in the API answer is
 * refused rather than followed.
 */
export const isTrustedCheckoutUrl = (url: string): boolean => {
  try {
    const { protocol, hostname } = new URL(url)
    return protocol === 'https:' && ['invoxia.com', 'myshopify.com', 'shopify.com'].some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
  } catch {
    return false
  }
}
